import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { ConvexProvider, ConvexReactClient } from "convex/react";

const DataSourceContext = createContext(null);

// Lazy initialization of Convex client
let convexClient = null;
const getConvexClient = () => {
  if (!convexClient && import.meta.env.VITE_CONVEX_URL) {
    try {
      convexClient = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL);
    } catch (error) {
      console.error("Failed to initialize Convex client:", error);
    }
  }
  return convexClient;
};

/**
 * DataSourceProvider keeps legacy data-source context APIs available
 * while pinning runtime behavior to Convex.
 */
export function DataSourceProvider({ children }) {
  const [dataSource, setDataSource] = useState("convex");

  const [isInitialized, setIsInitialized] = useState(false);

  // Persist data source selection to localStorage
  useEffect(() => {
    localStorage.setItem("karaoke_data_source", dataSource);
  }, [dataSource]);

  // Fetch data source from backend settings on mount
  useEffect(() => {
    const fetchDataSource = async () => {
      try {
        const response = await fetch("/api/settings/data-source");
        if (response.ok) {
          const data = await response.json();
          if (data.active === "convex") {
            setDataSource("convex");
          }
        }
      } catch (error) {
        // Backend might not be running or setting doesn't exist
        console.debug("Could not fetch data source setting:", error.message);
      } finally {
        setIsInitialized(true);
      }
    };

    fetchDataSource();
  }, []);

  // Handle data source change with optional page reload
  const handleSetDataSource = async (newSource, shouldReload = true) => {
    if (newSource !== "convex") {
      console.warn("Ignoring non-Convex data source request:", newSource);
      return;
    }

    // Persist to backend for global setting
    try {
      await fetch("/api/settings/data-source", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: newSource }),
        credentials: "include",
      });
    } catch (error) {
      console.error("Failed to persist data source setting:", error);
    }

    setDataSource(newSource);

    if (shouldReload) {
      // Small delay to allow state to persist
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const contextValue = useMemo(
    () => ({
      dataSource,
      setDataSource: handleSetDataSource,
      isConvex: dataSource === "convex",
      isSupabase: dataSource === "supabase",
      isInitialized,
    }),
    [dataSource, isInitialized]
  );

  // If using Convex, wrap with ConvexProvider
  if (dataSource === "convex") {
    const client = getConvexClient();
    if (client) {
      return (
        <DataSourceContext.Provider value={contextValue}>
          <ConvexProvider client={client}>{children}</ConvexProvider>
        </DataSourceContext.Provider>
      );
    } else {
      // Convex is required; keep context in Convex mode even if client init fails here.
      console.warn("Convex client not available; continuing in Convex mode");
      return (
        <DataSourceContext.Provider
          value={{ ...contextValue, dataSource: "convex", isConvex: true, isSupabase: false }}
        >
          {children}
        </DataSourceContext.Provider>
      );
    }
  }

  // Safety fallback: keep context pinned to Convex-only mode.
  return (
    <DataSourceContext.Provider value={{ ...contextValue, dataSource: "convex", isConvex: true, isSupabase: false }}>
      {children}
    </DataSourceContext.Provider>
  );
}

/**
 * Hook to access data source context
 */
export const useDataSource = () => {
  const context = useContext(DataSourceContext);
  if (!context) {
    throw new Error("useDataSource must be used within a DataSourceProvider");
  }
  return context;
};

/**
 * Hook to check if data source is initialized
 */
export const useDataSourceReady = () => {
  const { isInitialized } = useDataSource();
  return isInitialized;
};

export default DataSourceProvider;
